import axios from 'axios';
import { getAuthToken } from './auth';

// Base API URL - replace with your actual API URL in production
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    const token = await getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Fetch partner vouchers
 * @param {string} partnerId - The partner ID
 * @returns {Promise<Array>} - List of partner vouchers
 */
export const fetchPartnerVouchers = async (affiliateId) => {
  try {
    
    const response = await api.get(`/api/admin/affiliate/${affiliateId}/vouchers`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching partner vouchers:', error);
    throw error;
  }
};

/**
 * Create a new voucher for a partner
 * @param {string} partnerId - The partner ID
 * @param {string} voucherCode - The voucher code to create
 * @returns {Promise<Object>} - The created voucher object
 */
export const createVoucher = async (affiliateId, voucherCode, shortUrl) => {
  try {
    const response = await api.post(`/api/admin/voucher`, {
      affiliate_id: affiliateId,
      voucher_code: voucherCode,
      short_url: shortUrl
    });
    
    return response.data.data;
  } catch (error) {
    console.error('Error creating voucher:', error);
    throw error;
  }
};

export const createAffiliate = async (data) => {
  try {
    const response = await api.post(`/api/admin/affiliate`, data);
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create affiliate');
  }
};

export const fetchAffiliates = async () => {
  try {
    const response = await api.get('/api/admin/affiliate');
    return response.data.data;
  } catch (error) {
    throw error;
  }
};

export const fetchReferalManagerAffiliates = async (partnerName) => {
  try {
    const response = await api.get('/api/private/manager-affiliate?partner_name=' + partnerName);
    return response.data.data;
  } catch (error) {
    throw error;
  }
};

// Add this function to your existing api.js
/**
 * Fetch purchase summary for the authenticated user
 * @returns {Promise<Object>} - Purchase summary data
 */
export const fetchPurchaseSummary = async () => {
  try {
    // Use the new GET endpoint instead of POST
    const response = await api.get('/api/private/affiliate/summary');
    return response.data.data;
  } catch (error) {
    console.error('Error fetching purchase summary:', error);
    throw error;
  }
};

export const fetchAnalytics = async (partnerName) => {
  let url = '';
  try {
    if (partnerName) {
      url = '/api/private/affiliate/referral-analytics?partner_name=' + partnerName;
    } else {
      url = '/api/admin/affiliate/analytics';
    }
    const response = await api.get(url);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching analytics:', error);
    throw error;
  }
  
};

export const registerUser = async (email, password, name, role, partnerName) => {
  try {
    const response = await api.post('/api/admin/user/register', { 
      email: email, 
      password: password, 
      display_name: name,
      role: role,
      partner_name: partnerName
    });
    return response.data;
  } catch (error) {
    console.error('Register user error:', error);
    throw error;
  }
};

export const updateUser = async (uid, data) => {
  try {
    const response = await api.put(`/api/admin/user/${uid}`, data);
    return response.data.data;
  } catch (error) {
    console.error('Update user error:', error);
    throw error;
  }
};

export const updateAffiliate = async (id, data) => {
  try {
    const response = await api.put(`/api/admin/affiliate/${id}`, {
      ...data
    });
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update affiliate');
  }
};

export const fetchUsers = async () => {
  try {
    const response = await api.get('/api/admin/users');
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Delete a user by ID
 * @param {string} uid - The user ID to delete
 * @returns {Promise<Object>} - Response data
 */
export const deleteUser = async (uid) => {
  try {
    const response = await api.delete(`/api/admin/user/${uid}`);
    return response.data;
  } catch (error) {
    console.error('Delete user error:', error);
    throw error;
  }
};

export default api;
